import React from "react";
import { useTranslation } from "react-i18next";
import UserSettingsService, { SingleWorkspaceSettingsPaths } from "src/services/userSettingsService";

import { Box, BoxProps, Collapse, IconButton, Typography, useTheme } from "@mui/material";

interface CollapsibleGroupContextType {
    expandAll: boolean | null;
    setExpandAll: React.Dispatch<React.SetStateAction<boolean | null>>;
}
export const CollapsibleGroupContext = React.createContext<CollapsibleGroupContextType>({
    expandAll: null,
    setExpandAll: () => null,
});
type CollapsibleGroupProps = React.PropsWithChildren<{
    initialValue?: boolean | null;
}>;
export const CollapsibleGroupProvider = (props: CollapsibleGroupProps) => {
    const [expandAll, setExpandAll] = React.useState<boolean | null>(props.initialValue || null);

    return (
        <CollapsibleGroupContext.Provider value={{ expandAll, setExpandAll }}>{props.children}</CollapsibleGroupContext.Provider>
    );
};

type CollapsibleContextType = {
    open: boolean;
    disabled: boolean;
    setOpen: (open: boolean) => void;
};
const CollapsibleContext = React.createContext<CollapsibleContextType>({
    open: false,
    disabled: false,
    setOpen: () => null,
});
type CollapsibleProviderType = React.PropsWithChildren<{
    initialValue?: boolean;
    onChange?: (open: boolean) => void;
    disabled?: boolean;
}>;
export const CollapsibleProvider = (props: CollapsibleProviderType) => {
    const [open, setOpen] = React.useState(!!props.initialValue);
    const { expandAll, setExpandAll } = React.useContext(CollapsibleGroupContext);

    const onOpen = (value: boolean) => {
        setOpen(value);
        if (props.onChange) {
            props.onChange(value);
            setExpandAll(null);
        }
    };

    React.useEffect(() => {
        if (expandAll !== null) {
            setOpen(expandAll);
        }
        if (expandAll !== null && props.onChange) {
            setOpen(expandAll);
            props.onChange(expandAll);
        }
    }, [expandAll, props]);

    return (
        <CollapsibleContext.Provider value={{ open, setOpen: onOpen, disabled: props.disabled || false }}>
            <Box>{props.children}</Box>
        </CollapsibleContext.Provider>
    );
};

export const CollapsibleSectionSummary = ({ children, ...props }: React.PropsWithChildren<BoxProps>) => {
    const { t } = useTranslation(["common"]);
    const context = React.useContext(CollapsibleContext);

    if (!context) {
        throw new Error(t("common:error.collapsibleWrapper.noContext"));
    }

    return (
        <Box
            display="flex"
            alignItems="center"
            onClick={() => context.setOpen(!context.open)}
            style={{ cursor: "pointer" }}
            {...props}
        >
            <IconButton size="large">
                {context.open ? (
                    <i className="fa-solid fa-chevron-down" style={{ fontSize: 16 }} />
                ) : (
                    <i className="fa-solid fa-chevron-right" style={{ fontSize: 16 }} />
                )}
            </IconButton>
            <div style={{ width: "100%" }}>{children}</div>
        </Box>
    );
};

export const CollapsibleSectionDetails = (
    props: React.PropsWithChildren<{
        error?: string;
    }>,
) => {
    const context = React.useContext(CollapsibleContext);
    const theme = useTheme();
    const { t } = useTranslation(["common"]);

    if (!context) {
        throw new Error(t("common:error.collapsibleWrapper.useInsideContext"));
    }

    return (
        <Collapse in={context.open} collapsedSize={0}>
            {props.error ? (
                <Typography color="error" variant="body2">
                    {props.error}
                </Typography>
            ) : null}
            <Box
                style={{
                    padding: 0,
                    paddingTop: 0,
                    pointerEvents: context.disabled ? "none" : "auto",
                    opacity: context.disabled ? 0.5 : 1,
                    paddingBottom: theme.spacing(4),
                }}
            >
                {props.children}
            </Box>
        </Collapse>
    );
};

type CollapsibleSectionTitleProps = {
    error?: string;
    size?: "small" | "medium" | "large";
    withoutBorder?: boolean;
    bold?: boolean;
};
export const CollapsibleSectionTitle = ({
    size = "large",
    withoutBorder = false,
    bold = false,
    ...props
}: React.PropsWithChildren<CollapsibleSectionTitleProps>) => {
    const theme = useTheme();

    const getVariant = () => {
        switch (size) {
            case "small":
                return "body2";
            case "medium":
                return "subtitle1";
            case "large":
                return "h6";
        }
    };

    return (
        <Box
            style={{
                borderBottom: withoutBorder ? "none" : `1px solid ${theme.palette.emteria.border.primary}`,
                display: "flex",
                alignItems: "flex-end",
                gap: theme.spacing(2),
            }}
        >
            <Typography variant={getVariant()} style={{ fontWeight: bold ? 600 : 400 }}>
                {props.children}
            </Typography>
            {props.error ? (
                <Typography variant="caption" color="error">
                    {props.error}
                </Typography>
            ) : null}
        </Box>
    );
};

export const useCollapsiblePersistence = (storageKey: SingleWorkspaceSettingsPaths, sectionkey: string) => {
    const localStorageValue = () => UserSettingsService.getProvisioningPageCollapsedSections();
    const onChange = (open: boolean) => {
        const collapsedSection = localStorageValue();
        collapsedSection[sectionkey] = open;
        UserSettingsService.setWorkspaceValue(storageKey, collapsedSection);
    };
    return { value: localStorageValue()[sectionkey], onChange };
};
